import dayjs from 'dayjs';

import { IMyTicket } from '../ticketshop/types';

/**
 * LSE (Lüchow-Schmarsauer Eisenbahn GmbH) contact block shown on the chipcard
 * request screen. These are fixed proper nouns / postal + contact details of the
 * WendlandMobil operator, so they are not translated. The BE does not yet expose
 * operator contact data (the "pull from LSE" endpoint is a later ticket), hence
 * the hardcoded constant — swap for the BE field once available.
 */
export const LSE_CONTACT = {
	name: 'Lüchow-Schmarsauer Eisenbahn GmbH',
	addressLines: ['Königsberger Str. 10 (Kreishaus)', '29439 Lüchow (Wendland)'],
	phoneDisplay: '05841-120 555',
	phoneHref: 'tel:00495841120555',
	email: 'lse@luechow-dannenberg.de',
} as const;

/** Fixed German subject line of the chipcard request email (recipient is LSE). */
export const CHIPCARD_EMAIL_SUBJECT = 'Antrag auf Chipkarte';

/**
 * Full WendlandMobil zone set. A whole-network purchase (3+ zones) is flagged by
 * the BE with `wholeNetwork: true` but its `zoneCodes` still lists only the zones
 * the user tapped (e.g. A,B,C). LSE needs all zones in that case (per PM), so we
 * substitute this fixed list. WendlandMobil is currently the only chipcard
 * operator; revisit if that changes.
 */
export const WHOLE_NETWORK_ZONES = ['A', 'B', 'C', 'D', 'E'] as const;

/**
 * Build the prefilled German email body for the "Email-Formular Chipkarte"
 * button (Figma 25262-37154). The recipient (LSE) is a German authority, so the
 * message is German regardless of the app locale. Missing fields collapse to an
 * empty line so the template stays stable.
 */
export const buildChipcardEmailBody = (
	ticket: IMyTicket,
	customerEmail = ''
) => {
	// A whole-network purchase (3+ zones) means every zone, but the BE's
	// `zoneCodes` still lists only the tapped subset (e.g. A,B,C) — so substitute
	// the full network list (per PM). Otherwise send the exact booked zones.
	const zones = ticket.wholeNetwork
		? WHOLE_NETWORK_ZONES.join(',')
		: ticket.zoneCodes?.join(',') ?? '';
	const validUntil = ticket.validUntil
		? dayjs(ticket.validUntil).format('DD.MM.YYYY, HH:mm')
		: '';
	const passengerName = ticket.passengerName ?? '';

	return [
		'Guten Tag,',
		'',
		'hiermit beantrage ich eine Chipkarte für mein Jahresticket.',
		'',
		'mopla_Account-Email:',
		customerEmail,
		'',
		'Ticket-ID:',
		ticket.ticketCode ?? '',
		'',
		'Zonen:',
		zones,
		'',
		'End-Datum des Jahrestickets:',
		validUntil,
		'',
		'Bitte versenden Sie das Ticket an:',
		passengerName,
		'Bitte Ihre Versandadresse eingeben.',
		'',
		'Freundliche Grüße,',
		passengerName,
		// RFC 6068 mailto bodies use CRLF for line breaks; LF-only (`\n`) is
		// collapsed into one paragraph by some clients (notably iOS Mail).
	].join('\r\n');
};

/**
 * Compose the `mailto:` href (recipient + subject + prefilled body) opened by
 * the chipcard request button.
 */
export const buildChipcardMailtoHref = (
	ticket: IMyTicket,
	customerEmail = ''
): string =>
	`mailto:${LSE_CONTACT.email}?subject=${encodeURIComponent(
		CHIPCARD_EMAIL_SUBJECT
	)}&body=${encodeURIComponent(buildChipcardEmailBody(ticket, customerEmail))}`;
